/**
 * The home page's URL, both ways. The URL is the only description of
 * what the page shows: which view (path), which project (query),
 * which task is open in the panel (path) and whether it is being edited
 * (query), or the new-task form and what it starts with (query).
 *
 *   /
 *   /view/:viewId
 *   /view/:viewId?project=3
 *   /view/:viewId/tasks/:taskId[?edit=1]
 *   /view/:viewId/tasks/new[?project=3&date=2026-09-14&from=12]
 *
 * The tasks period (`dateFrom`/`dateTo`) is written by the filters
 * context, not here - the builders keep whatever else the query holds.
 */
import { NEW_TASK } from "@/constants/CTasks";

import { THomeLocation, THomePathTarget } from "@/types";

const PARAMS = {
	project: "project",
	edit: "edit",
	date: "date",
	from: "from",
} as const;

const OWN_PARAMS = Object.values(PARAMS) as string[];

const toId = (value: string | null | undefined): number | undefined => {
	if (!value) return undefined;
	const id = Number(value);
	return Number.isInteger(id) && id > 0 ? id : undefined;
};

const DATE = /^\d{4}-\d{2}-\d{2}$/;

/** What the URL says the home page shows. */
export const parseHomeLocation = (
	params: { viewId?: string; taskId?: string },
	search: URLSearchParams
): THomeLocation => {
	const isNewTask = params.taskId === NEW_TASK;
	const date = search.get(PARAMS.date) ?? undefined;
	return {
		viewId: toId(params.viewId),
		projectId: toId(search.get(PARAMS.project)),
		taskId: isNewTask ? undefined : toId(params.taskId),
		isNewTask,
		isEditing: search.get(PARAMS.edit) === "1",
		prefill: isNewTask
			? {
					projectId: toId(search.get(PARAMS.project)),
					date: date && DATE.test(date) ? date : undefined,
					fromTaskId: toId(search.get(PARAMS.from)),
				}
			: {},
	};
};

/**
 * Builds a home URL. `keep` is the current query: everything that is
 * not one of the home params (the tasks period, for one) is carried
 * over. No `viewId` = "/", the default view.
 */
export const homePath = (
	target: THomePathTarget,
	keep?: URLSearchParams
): string => {
	const search = new URLSearchParams();
	keep?.forEach((value, key) => {
		if (!OWN_PARAMS.includes(key)) search.set(key, value);
	});
	if (target.taskId === NEW_TASK) {
		const { projectId, date, fromTaskId } = target.prefill ?? {};
		if (projectId) search.set(PARAMS.project, String(projectId));
		if (date) search.set(PARAMS.date, date);
		if (fromTaskId) search.set(PARAMS.from, String(fromTaskId));
	} else {
		if (target.projectId)
			search.set(PARAMS.project, String(target.projectId));
		if (target.taskId && target.edit) search.set(PARAMS.edit, "1");
	}

	let path = target.viewId ? `/view/${target.viewId}` : "/";
	if (target.taskId && target.viewId) path += `/tasks/${target.taskId}`;
	const query = search.toString();
	return query ? `${path}?${query}` : path;
};
